import React, { useState, useEffect, useCallback } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Badge } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, TimePicker } from '@mui/x-date-pickers';
import { CURRENT_DATE, SELECTED_TIME } from 'store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Add timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

function ServerDay(props) {
  const { highlightedDays = [], day, outsideCurrentMonth, selectedDate, ...other } = props;

  // Check if this day has available data
  const isAvailable = !outsideCurrentMonth && highlightedDays.includes(day.getDate());

  // Check if this day is the currently selected date
  const isSelected =
    !outsideCurrentMonth &&
    selectedDate &&
    day.getDate() === selectedDate.getDate() &&
    day.getMonth() === selectedDate.getMonth() &&
    day.getFullYear() === selectedDate.getFullYear();

  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      sx={{
        margin: '2px',
        borderRadius: '50%',
        '& .MuiBadge-dot': {
          backgroundColor: isAvailable ? '#44be54' : 'transparent',
          width: '8px',
          height: '8px'
        }
      }}
      badgeContent={isSelected || isAvailable ? '' : undefined}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      variant="dot"
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        sx={{
          ...(isSelected && {
            backgroundColor: '#44be54 !important',
            '&:hover': {
              backgroundColor: '#44be54 !important'
            }
          }),
          ...(isAvailable &&
            !isSelected && {
              backgroundColor: '#e8f5e8 !important',
              '&:hover': {
                backgroundColor: '#c8e6c9 !important'
              }
            })
        }}
      />
    </Badge>
  );
}

function DatePickerStore() {
  const date = useSelector((state) => state.customization.currentDate);
  const [calendar, setCalendar] = useState(new Date(date));
  const [highlightedDays, setHighlightedDays] = useState([]);
  const [availableDatesData, setAvailableDatesData] = useState([]);
  const dispatch = useDispatch();
  const selectedTime = useSelector((state) => state.customization.selectedTime);
  const source = (localStorage.getItem('source'));

  // Helper function to get 8 AM IST as local time
  const get8AMISTAsLocal = (dateForTime) => {
    const istTime = dayjs(dateForTime).tz('Asia/Kolkata').hour(0).minute(0).second(0);
    return istTime.local();
  };

  // Initialize times
  const [startTime, setStartTime] = useState(get8AMISTAsLocal(calendar));
  const [endTime, setEndTime] = useState(() => {
    const isToday = dayjs(calendar).isSame(dayjs(), 'day');
    return isToday ? dayjs() : dayjs(calendar).endOf('day');
  });

  const [isLoading, setIsLoading] = useState(false);

  // Calculate minimum end time (start time + 16 minutes)
  const getMinimumEndTime = useCallback(() => {
    return startTime.add(16, 'minute');
  }, [startTime]);

  // Calculate maximum end time based on selected date
  const getMaximumEndTime = useCallback(() => {
    const isToday = dayjs(calendar).isSame(dayjs(), 'day');
    return isToday ? dayjs() : dayjs(calendar).endOf('day');
  }, [calendar]);

  // Check if end time should be disabled
  const isEndTimeDisabled = useCallback(() => {
    const minEndTime = getMinimumEndTime();
    const maxEndTime = getMaximumEndTime();
    
    // Disable if minimum required time (start + 16 min) is after maximum allowed time
    return minEndTime.isAfter(maxEndTime);
  }, [getMinimumEndTime, getMaximumEndTime]);

  const fetchAvailableDates = useCallback(
    async (selectedDate) => {
      try {
        setIsLoading(true);

        const response = await fetch('https://db.neophyte.live/sentinel-api/api/sentinel/getDataByMonth', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            source_id: source
          })
        });

        if (!response.ok) {
          throw new Error('Failed to fetch available dates');
        }

        const result = await response.json();

        if (result.status === 'success' && result.data) {
          setAvailableDatesData(result.data);
          updateHighlightedDaysForMonth(selectedDate, result.data);
        }
      } catch (error) {
        console.error('Error fetching available dates:', error);
        setAvailableDatesData([]);
        setHighlightedDays([]);
      } finally {
        setIsLoading(false);
      }
    },
    [source]
  );

  const updateHighlightedDaysForMonth = useCallback((currentDate, apiData) => {
    const currentMonth = dayjs(currentDate).format('YYYY-MM');
    const daysToHighlight = [];

    const currentMonthData = apiData.find((monthData) => monthData._id === currentMonth);

    if (currentMonthData && currentMonthData.dates) {
      currentMonthData.dates.forEach((dateString) => {
        const date = dayjs(dateString);
        if (date.format('YYYY-MM') === currentMonth) {
          daysToHighlight.push(date.date());
        }
      });
    }

    setHighlightedDays(daysToHighlight);
  }, []);

  const fetchDataByTimeRange = useCallback(
    async (startTime, endTime) => {
      try {
        setIsLoading(true);
        const startTimestamp = startTime.toDate().getTime();
        const endTimestamp = endTime.toDate().getTime();

        const response = await fetch(`/api/data?startTime=${startTimestamp}&endTime=${endTimestamp}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }

        const data = await response.json();

        dispatch({
          type: 'FETCH_DATA_SUCCESS',
          payload: {
            data,
            timeRange: {
              startTime: startTimestamp,
              endTime: endTimestamp
            }
          }
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        dispatch({
          type: 'FETCH_DATA_ERROR',
          payload: error.message
        });
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (startTime && endTime && endTime.isAfter(startTime)) {
      const timeoutId = setTimeout(() => {
        fetchDataByTimeRange(startTime, endTime);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [startTime, endTime, fetchDataByTimeRange]);

  useEffect(() => {
    fetchAvailableDates(calendar);
  }, [fetchAvailableDates, calendar]);

  useEffect(() => {
    const startTimeString = `${padTo2Digits(startTime.hour())}:${padTo2Digits(startTime.minute())}`;
    const endTimeString = `${padTo2Digits(endTime.hour())}:${padTo2Digits(endTime.minute())}`;

    dispatch({
      type: SELECTED_TIME,
      selectedTime: {
        start_time: startTime.toDate().getTime(),
        start_time_string: formatDate(calendar, startTimeString),
        end_time: endTime.toDate().getTime(),
        end_time_string: formatDate(calendar, endTimeString)
      }
    });
  }, []);

  // Auto-adjust end time when start time changes
  useEffect(() => {
    const minEndTime = getMinimumEndTime();
    const maxEndTime = getMaximumEndTime();
    
    // If current end time is less than minimum required (start + 16 min), adjust it
    if (endTime.isBefore(minEndTime)) {
      if (minEndTime.isBefore(maxEndTime) || minEndTime.isSame(maxEndTime)) {
        setEndTime(minEndTime);
      } else {
        // If minimum required time exceeds maximum allowed time, set to maximum
        setEndTime(maxEndTime);
      }
    }
  }, [startTime, endTime, getMinimumEndTime, getMaximumEndTime]);

  const handleMonthChange = useCallback(
    (date) => {
      fetchAvailableDates(date);
    },
    [fetchAvailableDates]
  );

  const padTo2Digits = (num) => {
    return num.toString().padStart(2, '0');
  };

  const formatDate = (date, time) => {
    return [date.getFullYear(), padTo2Digits(date.getMonth() + 1), padTo2Digits(date.getDate())].join('-') + 'T' + time + ':00.000Z';
  };

  const handleStartTimeChange = (newTime) => {
    const timeWithCorrectDate = dayjs(calendar).hour(newTime.hour()).minute(newTime.minute()).second(0);
    setStartTime(timeWithCorrectDate);

    const timeString = `${padTo2Digits(timeWithCorrectDate.hour())}:${padTo2Digits(timeWithCorrectDate.minute())}`;
    dispatch({
      type: SELECTED_TIME,
      selectedTime: {
        ...selectedTime,
        start_time: timeWithCorrectDate.toDate().getTime(),
        start_time_string: formatDate(calendar, timeString)
      }
    });
    dispatch({
      type: CURRENT_DATE,
      currentDate: formatDate(calendar, timeString)
    });
  };

  const handleEndTimeChange = (newTime) => {
    const currentDateTime = dayjs();
    const selectedDateTime = dayjs(calendar).hour(newTime.hour()).minute(newTime.minute()).second(0);

    if (selectedDateTime.isAfter(currentDateTime)) {
      console.warn('Future times are not allowed');
      return;
    }

    // Check if selected end time is at least 16 minutes after start time
    const minEndTime = getMinimumEndTime();
    if (selectedDateTime.isBefore(minEndTime)) {
      console.warn('End time must be at least 16 minutes after start time');
      return;
    }

    const timeWithCorrectDate = dayjs(calendar).hour(newTime.hour()).minute(newTime.minute()).second(0);
    setEndTime(timeWithCorrectDate);

    const timeString = `${padTo2Digits(timeWithCorrectDate.hour())}:${padTo2Digits(timeWithCorrectDate.minute())}`;
    dispatch({
      type: SELECTED_TIME,
      selectedTime: {
        ...selectedTime,
        end_time: timeWithCorrectDate.toDate().getTime(),
        end_time_string: formatDate(calendar, timeString)
      }
    });
  };

  useEffect(() => {
    const timeString = `${padTo2Digits(startTime.hour())}:${padTo2Digits(startTime.minute())}`;
    dispatch({ type: CURRENT_DATE, currentDate: formatDate(calendar, timeString) });
  }, [calendar, startTime, dispatch]);

  return (
    <div className="flex items-center justify-end gap-2 w-[400px]">
      <div className="flex gap-2 w-80">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            label="Start Time"
            value={startTime}
            onChange={handleStartTimeChange}
            disabled={isLoading}
            sx={{
              bgcolor: 'white',
              '& .MuiInputBase-root': {
                height: '40px'
              }
            }}
          />
          <TimePicker
            label="End Time"
            value={endTime}
            onChange={handleEndTimeChange}
            disabled={isLoading || isEndTimeDisabled()}
            minTime={getMinimumEndTime()}
            maxTime={getMaximumEndTime()}
            sx={{
              bgcolor: 'white',
              '& .MuiInputBase-root': {
                height: '40px'
              }
            }}
          />
        </LocalizationProvider>
      </div>
      <div className="w-52">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            className="cursor-pointer"
            disabled={isLoading}
            maxDate={new Date()}
            shouldDisableDate={(date) => date > new Date()}
            sx={{
              bgcolor: 'white',
              paddingTop: 0,
              '& .MuiInputBase-root': {
                height: '40px',
                width: '100%'
              }
            }}
            slots={{
              day: ServerDay
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                bgcolor: 'white'
              },
              day: {
                highlightedDays,
                selectedDate: calendar
              }
            }}
            format="dd/MM/yyyy"
            value={calendar}
            onChange={(newDate) => {
              if (newDate) {
                const selectedDate = new Date(newDate);
                setCalendar(selectedDate);

                const newStartTime = get8AMISTAsLocal(selectedDate);
                setStartTime(newStartTime);

                const isToday = dayjs(selectedDate).isSame(dayjs(), 'day');
                const newEndTime = isToday ? dayjs() : dayjs(selectedDate).endOf('day');
                
                // Ensure end time is at least 16 minutes after start time
                const minEndTime = newStartTime.add(16, 'minute');
                const finalEndTime = newEndTime.isBefore(minEndTime) ? minEndTime : newEndTime;
                setEndTime(finalEndTime);

                updateHighlightedDaysForMonth(selectedDate, availableDatesData);

                const startTimeString = `${padTo2Digits(newStartTime.hour())}:${padTo2Digits(newStartTime.minute())}`;
                const endTimeString = `${padTo2Digits(finalEndTime.hour())}:${padTo2Digits(finalEndTime.minute())}`;

                dispatch({
                  type: SELECTED_TIME,
                  selectedTime: {
                    start_time: newStartTime.toDate().getTime(),
                    start_time_string: formatDate(selectedDate, startTimeString),
                    end_time: finalEndTime.toDate().getTime(),
                    end_time_string: formatDate(selectedDate, endTimeString)
                  }
                });

                dispatch({
                  type: CURRENT_DATE,
                  currentDate: formatDate(selectedDate, startTimeString)
                });
              }
            }}
            onMonthChange={handleMonthChange}
          />
        </LocalizationProvider>
      </div>
      {/* {isLoading && <div className="text-sm text-gray-500">Loading data...</div>} */}
      {/* {isEndTimeDisabled() && (
        <div className="text-sm text-orange-500">
          End time unavailable (needs 16+ min gap)
        </div>
      )} */}
    </div>
  );
}

export default DatePickerStore;